/**
 * Media Library — 2026-07 refresh: big clear thumbnails, client + type
 * filters, preview with "Use in Composer" deep link. Display layer only —
 * upload/list/delete endpoints unchanged (upload now just tags an optional
 * client_group_id).
 */
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { mediaAPI, clientGroupAPI, toCurrentOrigin } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { StellarSelect } from '../components/StellarFormControls';
import {
  Upload, Image as ImageIcon, Film, FileText, Music,
  Trash2, Download, Search, PenSquare, Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const ALL = '__all__';

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const kindOf = (ct) => {
  if (ct?.startsWith('image/')) return 'image';
  if (ct?.startsWith('video/')) return 'video';
  return 'document';
};

function Thumb({ item, wsId }) {
  const kind = kindOf(item.content_type);
  const src = toCurrentOrigin(item.thumbnail_url || item.public_url) || mediaAPI.download(wsId, item.id);
  if (kind === 'image') {
    return <img src={src} alt={item.filename} loading="lazy" className="w-full h-full object-cover" />;
  }
  if (kind === 'video') {
    return (
      <div className="relative w-full h-full bg-black/40">
        {item.thumbnail_url ? (
          <img src={toCurrentOrigin(item.thumbnail_url)} alt={item.filename} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Film className="h-10 w-10 text-muted-foreground" /></div>
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="h-10 w-10 rounded-full bg-black/60 flex items-center justify-center">
            <Play className="h-5 w-5 text-white ml-0.5" />
          </span>
        </span>
      </div>
    );
  }
  const Icon = item.content_type?.startsWith('audio/') ? Music : FileText;
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-3 bg-foreground/[0.03]">
      <Icon className="h-10 w-10 text-gold/60" />
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {(item.extension || '').replace('.', '') || 'file'}
      </span>
    </div>
  );
}

export default function MediaLibraryPage() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.id;
  const [media, setMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [clientFilter, setClientFilter] = useState(ALL);
  const [uploadClient, setUploadClient] = useState(ALL);
  const [clients, setClients] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const fileInputRef = useRef(null);

  const fetchMedia = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const res = await mediaAPI.list(wsId, 0, 200);
      setMedia(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);
  useEffect(() => {
    if (!wsId) return;
    clientGroupAPI.list(wsId).then((r) => setClients(r?.data || [])).catch(() => {});
  }, [wsId]);

  const clientNameById = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [clients]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    for (const file of files) {
      setUploading(true);
      setUploadProgress(0);
      try {
        await mediaAPI.upload(wsId, file, (event) => {
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        }, uploadClient === ALL ? null : uploadClient);
        toast.success(`Uploaded ${file.name}`);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    setUploadProgress(0);
    fetchMedia();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (item) => {
    try {
      await mediaAPI.delete(wsId, item.id);
      toast.success('Deleted');
      setPreviewItem(null);
      fetchMedia();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const useInComposer = (item) => {
    const kind = kindOf(item.content_type);
    const params = new URLSearchParams();
    if (item.client_group_id) params.set('client', item.client_group_id);
    params.set('media_id', item.id);
    params.set('media_url', toCurrentOrigin(item.public_url) || mediaAPI.download(wsId, item.id));
    params.set('media_type', kind === 'video' ? 'video' : 'image');
    params.set('media_name', item.filename || 'media');
    navigate(`/compose?${params.toString()}`);
  };

  const filtered = media.filter((m) => {
    if (search && !m.filename?.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== ALL && kindOf(m.content_type) !== typeFilter) return false;
    if (clientFilter !== ALL && m.client_group_id !== clientFilter) return false;
    return true;
  });

  const clientOptions = useMemo(
    () => [{ value: ALL, label: 'All clients' }].concat(clients.map((c) => ({ value: c.id, label: c.name || 'Unnamed' }))),
    [clients],
  );

  return (
    <div className="p-6 space-y-5 animate-fade-in-up" data-testid="media-library-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Media Library</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} files · click any file to preview or send it to the Composer.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-44">
            <StellarSelect value={uploadClient} onValueChange={setUploadClient}
                           options={[{ value: ALL, label: 'Tag client (optional)' }].concat(clients.map((c) => ({ value: c.id, label: c.name || 'Unnamed' })))}
                           placeholder="Tag client (optional)" testId="media-upload-client" />
          </div>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload}
                 data-testid="media-library-upload-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="media-upload-button">
            <Upload className="h-4 w-4 mr-2" /> Upload
          </Button>
        </div>
      </div>

      {uploading && (
        <Card className="bg-card/60">
          <CardContent className="p-4 flex items-center gap-3">
            <Upload className="h-4 w-4 text-primary animate-pulse" />
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1"><span>Uploading...</span><span>{uploadProgress}%</span></div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search files..." value={search} onChange={(e) => setSearch(e.target.value)}
                 className="pl-9" data-testid="media-search" />
        </div>
        <div className="flex items-center gap-1" data-testid="media-type-tabs">
          {[[ALL, 'All'], ['image', 'Images'], ['video', 'Videos'], ['document', 'Documents']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setTypeFilter(v)}
                    className={`h-7 px-2.5 rounded-full text-[11px] border transition-colors ${
                      typeFilter === v ? 'bg-gold/15 border-gold/60 text-gold' : 'border-border/40 text-muted-foreground hover:border-border'
                    }`}
                    data-testid={`media-type-${v === ALL ? 'all' : v}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="w-44">
          <StellarSelect value={clientFilter} onValueChange={setClientFilter} options={clientOptions}
                         placeholder="All clients" testId="media-client-filter" />
        </div>
        <span className="text-[11px] text-muted-foreground">{filtered.length} shown</span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => <div key={i} className="aspect-square bg-secondary rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20" data-testid="media-empty">
          <ImageIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg font-medium">No files match</p>
          <p className="text-sm text-muted-foreground mt-1">Upload media or clear the filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
          {filtered.map((item) => (
            <div key={item.id}
                 className="group rounded-xl border border-border/40 bg-card/60 overflow-hidden hover:border-gold/50 transition-colors cursor-pointer"
                 onClick={() => setPreviewItem(item)}
                 data-testid={`media-tile-${item.id}`}>
              <div className="aspect-square overflow-hidden">
                <Thumb item={item} wsId={wsId} />
              </div>
              <div className="p-2">
                <p className="text-[12px] font-medium truncate">{item.filename}</p>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[10px] text-muted-foreground">{formatSize(item.file_size)}</span>
                  {item.client_group_id && clientNameById[item.client_group_id] && (
                    <Badge variant="outline" className="text-[9px] text-gold/80 border-gold/30 max-w-[110px] truncate">
                      {clientNameById[item.client_group_id]}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewItem} onOpenChange={() => setPreviewItem(null)}>
        <DialogContent className="max-w-3xl" data-testid="media-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{previewItem?.filename}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {previewItem && kindOf(previewItem.content_type) === 'image' ? (
              <img src={toCurrentOrigin(previewItem.public_url) || mediaAPI.download(wsId, previewItem.id)} alt={previewItem.filename}
                   className="w-full rounded-lg max-h-[55vh] object-contain bg-black/20" />
            ) : previewItem && kindOf(previewItem.content_type) === 'video' ? (
              <video src={toCurrentOrigin(previewItem.public_url) || mediaAPI.download(wsId, previewItem.id)} controls
                     className="w-full rounded-lg max-h-[55vh] bg-black" />
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Preview not available for this file type</p>
              </div>
            )}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">
                {formatSize(previewItem?.file_size)} · {previewItem?.content_type}
                {previewItem?.client_group_id && clientNameById[previewItem.client_group_id]
                  ? ` · ${clientNameById[previewItem.client_group_id]}` : ''}
              </span>
              <div className="flex gap-2">
                {previewItem && kindOf(previewItem.content_type) !== 'document' && (
                  <Button size="sm" onClick={() => useInComposer(previewItem)} data-testid="media-use-in-composer">
                    <PenSquare className="h-3.5 w-3.5 mr-1.5" /> Use in Composer
                  </Button>
                )}
                <Button size="sm" variant="outline"
                        onClick={() => window.open(toCurrentOrigin(previewItem?.public_url) || mediaAPI.download(wsId, previewItem?.id), '_blank')}
                        data-testid="media-download">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                </Button>
                <Button size="sm" variant="outline" className="text-red-300 border-red-500/30"
                        onClick={() => handleDelete(previewItem)} data-testid="media-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
